import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../components/Modal';
import {
  useProjectRequests,
  useCreateProjectRequest,
  useUpdateProjectRequest,
  useDeleteProjectRequest,
  useProjectRequestStatuses,
  useProjectRequestVouchers,
  useLinkVoucherToProjectRequest,
  useUnlinkVoucherFromProjectRequest,
  useCompanyVouchers,
  useRefreshCompanyVouchers,
  useClockodoSummary,
  useClockodoProjects,
} from '../hooks';
import { activitiesApi } from '../api';
import { useCrmSettings } from '../settings/CrmSettingsContext';
import type {
  CompanyProjectRequest,
  ProjectRequestStatus,
  ProjectPhase,
  CreateCompanyProjectRequestPayload,
  Activity,
  ActivityType,
  LexwareVoucher,
  VoucherType,
  ClockodoProject,
} from '../types';
import { VOUCHER_TYPE_LABELS } from '../types';
import styles from './CompanyDetailPage.module.css';

// ── Helpers ──

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatAmount(raw: string | null, currency = 'EUR'): string {
  if (!raw) return '—';
  const n = parseFloat(raw);
  if (isNaN(n)) return '—';
  return n.toLocaleString('de-DE', { style: 'currency', currency });
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0h';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  NOTE: 'Notiz', CALL: 'Anruf', EMAIL: 'E-Mail',
  MEETING: 'Meeting', TASK: 'Aufgabe', FOLLOWUP: 'Follow-Up',
  APPOINTMENT: 'Termin', SOCIAL_MEDIA: 'Social Media',
  MESSENGER: 'Messenger', EVENT: 'Event – Teilnahme',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const PRE_SALES_TYPES: VoucherType[] = ['QUOTATION', 'ORDER_CONFIRMATION'];
const POST_SALES_TYPES: VoucherType[] = ['INVOICE', 'CREDIT_NOTE'];

// ── Styles ──

const labelStyle: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text)',
  marginBottom: '0.25rem', display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem 0.625rem',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
  fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  background: 'var(--color-bg-card)', color: 'var(--color-text)',
};

const sectionLabel: React.CSSProperties = {
  fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-muted)',
};

// ── VoucherRow ──

function VoucherRow({
  voucher,
  onUnlink,
}: {
  voucher: LexwareVoucher;
  onUnlink?: () => void;
}) {
  const label = voucher.title || voucher.voucherNumber || '—';
  const isPreSales = PRE_SALES_TYPES.includes(voucher.voucherType);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.375rem 0.625rem',
      background: 'var(--color-bg-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-sm)',
      fontSize: '0.8125rem', gap: '0.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
        <span style={{
          fontSize: '0.625rem', fontWeight: 600, padding: '0.0625rem 0.375rem',
          borderRadius: '999px', flexShrink: 0,
          background: isPreSales ? '#dbeafe' : '#dcfce7',
          color: isPreSales ? '#1e40af' : '#166534',
        }}>
          {VOUCHER_TYPE_LABELS[voucher.voucherType]}
        </span>
        {voucher.lexwareDeepLink ? (
          <a
            href={voucher.lexwareDeepLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-primary)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {label}
          </a>
        ) : (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
          {formatDate(voucher.voucherDate)}
        </span>
        <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>
          {formatAmount(voucher.totalGrossAmount, voucher.currency)}
        </span>
        {onUnlink && (
          <button
            type="button"
            onClick={onUnlink}
            title="Verknüpfung entfernen"
            style={{
              width: 20, height: 20, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none',
              cursor: 'pointer', color: 'var(--color-error, #dc2626)',
            }}
          >
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ── VoucherLinkPicker ──

function VoucherLinkPicker({
  companyId,
  projectRequestId,
  typeFilter,
  onClose,
}: {
  companyId: string;
  projectRequestId: string;
  typeFilter: 'pre-sales' | 'post-sales';
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const linkMut = useLinkVoucherToProjectRequest();
  const refreshMut = useRefreshCompanyVouchers();
  const voucherTypes = typeFilter === 'pre-sales' ? PRE_SALES_TYPES : POST_SALES_TYPES;
  const companyVouchersQuery = useCompanyVouchers(companyId, { pageSize: 200 });
  const companyVouchers: LexwareVoucher[] = companyVouchersQuery.data?.data ?? [];

  const available = useMemo(() =>
    companyVouchers.filter((v) =>
      voucherTypes.includes(v.voucherType) &&
      !v.projectRequestId &&
      (!search || (v.title ?? v.voucherNumber ?? '').toLowerCase().includes(search.toLowerCase()))
    ),
    [companyVouchers, voucherTypes, search],
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: 560, maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
            Beleg zuordnen — {typeFilter === 'pre-sales' ? 'Angebot / Auftragsbestätigung' : 'Rechnung / Gutschrift'}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => refreshMut.mutate(companyId)}
              disabled={refreshMut.isPending}
              title="Belege aus Lexware aktualisieren"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: refreshMut.isPending ? 'wait' : 'pointer', color: 'var(--color-text-secondary)', opacity: refreshMut.isPending ? 0.6 : 1 }}
            >
              {refreshMut.isPending ? 'Aktualisieren...' : 'Belege aktualisieren'}
            </button>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--color-text-muted)', padding: '0.25rem' }}>×</button>
          </div>
        </div>
        <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
          <input type="text" placeholder="Suchen..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus style={inputStyle} />
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 1.5rem' }}>
          {companyVouchersQuery.isLoading ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Laden...</p>
          ) : available.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', fontStyle: 'italic' }}>Keine verfügbaren Belege</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {available.map((v) => (
                <div
                  key={v.id}
                  onClick={() => linkMut.mutate({ projectRequestId, voucherId: v.id }, { onSuccess: onClose })}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem', background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                    cursor: linkMut.isPending ? 'wait' : 'pointer', fontSize: '0.8125rem', gap: '0.5rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                    <span style={{ fontSize: '0.625rem', fontWeight: 600, padding: '0.0625rem 0.375rem', borderRadius: '999px', background: '#dbeafe', color: '#1e40af', flexShrink: 0 }}>
                      {VOUCHER_TYPE_LABELS[v.voucherType]}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {v.title || v.voucherNumber || '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{formatDate(v.voucherDate)}</span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(v.totalGrossAmount, v.currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ProjektberichtBlock — Level 1.1 ──

function ProjektberichtBlock({
  companyId,
  pr,
  lexwareEnabled,
}: {
  companyId: string;
  pr: CompanyProjectRequest;
  lexwareEnabled: boolean;
}) {
  const allTimeParams = useMemo(() => ({
    timeSince: '2010-01-01',
    timeUntil: new Date().toISOString().substring(0, 10),
    projectsId: pr.clockodoProjectId ?? undefined,
  }), [pr.clockodoProjectId]);
  const summaryQuery = useClockodoSummary(companyId, pr.clockodoProjectId ? allTimeParams : undefined);
  const summary = summaryQuery.data?.data;
  const projectSummary = summary?.byProject?.[0];

  const projectsQuery = useClockodoProjects(companyId);
  const clockodoProjects: ClockodoProject[] = projectsQuery.data?.data ?? [];

  const updatePR = useUpdateProjectRequest(companyId);
  const [linking, setLinking] = useState(false);

  const unlinkMut = useUnlinkVoucherFromProjectRequest();
  const [invoicePicker, setInvoicePicker] = useState(false);

  const postSalesQuery = useProjectRequestVouchers(pr.id, { pageSize: 50 });
  const postSalesVouchers: LexwareVoucher[] = (postSalesQuery.data?.data ?? []).filter(
    (v) => POST_SALES_TYPES.includes(v.voucherType),
  );

  function linkClockodo(clockodoProjectId: number) {
    updatePR.mutate({ id: pr.id, data: { clockodoProjectId } }, {
      onSuccess: () => setLinking(false),
    });
  }

  function unlinkClockodo() {
    if (window.confirm('Clockodo-Verknüpfung entfernen?')) {
      updatePR.mutate({ id: pr.id, data: { clockodoProjectId: null } });
    }
  }

  return (
    <div style={{
      marginLeft: '1.5rem',
      borderLeft: '3px solid var(--color-primary)',
      paddingLeft: '1rem',
      paddingTop: '0.75rem',
      paddingBottom: '0.75rem',
      background: 'var(--color-bg)',
      borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ ...sectionLabel, color: 'var(--color-primary)' }}>
            Projektbericht (Clockodo)
          </span>
          {pr.clockodoProjectId && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              #{pr.clockodoProjectId}
            </span>
          )}
        </div>
        {pr.clockodoProjectId ? (
          <button
            type="button"
            onClick={unlinkClockodo}
            style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
          >
            Trennen
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setLinking((p) => !p)}
            style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600 }}
          >
            Projekt verknüpfen
          </button>
        )}
      </div>

      {/* Clockodo project picker */}
      {linking && !pr.clockodoProjectId && (
        <div style={{ marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {projectsQuery.isLoading ? (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Laden...</span>
          ) : clockodoProjects.length === 0 ? (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              Keine Clockodo-Projekte für dieses Unternehmen gefunden
            </span>
          ) : (
            <>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.125rem' }}>
                Clockodo-Projekt auswählen:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: 160, overflow: 'auto' }}>
                {clockodoProjects.map((cp) => (
                  <div
                    key={cp.id}
                    onClick={() => linkClockodo(cp.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.375rem 0.625rem',
                      background: 'var(--color-bg-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: updatePR.isPending ? 'wait' : 'pointer',
                      fontSize: '0.8125rem',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.borderColor = 'var(--color-primary)')}
                    onMouseOut={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                  >
                    <span style={{ fontWeight: 500 }}>{cp.name}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>#{cp.id}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Clockodo hours summary */}
      {pr.clockodoProjectId && (
        <div style={{ marginBottom: '0.625rem' }}>
          {summaryQuery.isLoading ? (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Laden...</span>
          ) : summary ? (
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8125rem' }}>
              <div>
                <span style={{ color: 'var(--color-text-muted)' }}>Stunden gesamt: </span>
                <strong>{formatDuration(projectSummary?.totalSeconds ?? 0)}</strong>
              </div>
              {(projectSummary?.billableSeconds ?? 0) > 0 && (
                <div>
                  <span style={{ color: 'var(--color-text-muted)' }}>Davon abrechenbar: </span>
                  <strong>{formatDuration(projectSummary?.billableSeconds ?? 0)}</strong>
                </div>
              )}
            </div>
          ) : (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Keine Daten</span>
          )}
        </div>
      )}

      {/* Level 1.1.1 — ERP Belege (Rechnungen) */}
      {lexwareEnabled && (
        <div style={{
          marginLeft: '1rem',
          borderLeft: '2px solid var(--color-border)',
          paddingLeft: '0.875rem',
          paddingTop: '0.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
            <span style={sectionLabel}>ERP Belege — Rechnungen ({postSalesVouchers.length})</span>
            <button
              type="button"
              onClick={() => setInvoicePicker(true)}
              style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600 }}
            >
              + Rechnung zuordnen
            </button>
          </div>
          {postSalesQuery.isLoading ? (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Laden...</div>
          ) : postSalesVouchers.length === 0 ? (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              Keine Rechnungen zugeordnet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {postSalesVouchers.map((v) => (
                <VoucherRow
                  key={v.id}
                  voucher={v}
                  onUnlink={() => unlinkMut.mutate({ projectRequestId: pr.id, voucherId: v.id })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {invoicePicker && (
        <VoucherLinkPicker
          companyId={companyId}
          projectRequestId={pr.id}
          typeFilter="post-sales"
          onClose={() => setInvoicePicker(false)}
        />
      )}
    </div>
  );
}

// ── ProjectListItem — Level 1 ──

function ProjectListItem({
  pr,
  companyId,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  lexwareEnabled,
}: {
  pr: CompanyProjectRequest;
  companyId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  lexwareEnabled: boolean;
}) {
  const [preSalesPicker, setPreSalesPicker] = useState(false);
  const unlinkMut = useUnlinkVoucherFromProjectRequest();

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ['crm', 'activities', 'projectRequest', pr.id],
    queryFn: () => activitiesApi.list({ projectRequestId: pr.id, pageSize: 50 }),
    enabled: isExpanded,
  });
  const linkedActivities: Activity[] = activitiesData?.data ?? [];

  const preSalesQuery = useProjectRequestVouchers(isExpanded ? pr.id : '', { pageSize: 50 });
  const preSalesVouchers: LexwareVoucher[] = (preSalesQuery.data?.data ?? []).filter(
    (v) => PRE_SALES_TYPES.includes(v.voucherType),
  );

  const dateRange = [formatDate(pr.projectStart), pr.projectEnd ? formatDate(pr.projectEnd) : null]
    .filter(Boolean).join(' – ');

  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
      background: 'var(--color-bg-card)',
    }}>
      {/* Item header — Level 1 row */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.75rem 1rem', cursor: 'pointer',
          background: isExpanded ? 'var(--color-bg)' : 'var(--color-bg-card)',
          borderBottom: isExpanded ? '1px solid var(--color-border)' : 'none',
        }}
        onClick={onToggleExpand}
      >
        {/* Expand arrow */}
        <svg
          width="10" height="10" viewBox="0 0 10 10"
          style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'var(--color-text-muted)', flexShrink: 0 }}
        >
          <path d="M3 1l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        {/* Title + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--color-text)' }}>
              {pr.title}
            </span>
            {pr.status && (
              <span style={{
                fontSize: '0.6875rem', fontWeight: 600,
                padding: '0.125rem 0.5rem', borderRadius: '999px',
                background: pr.status.color + '20', color: pr.status.color,
                whiteSpace: 'nowrap',
              }}>
                {pr.status.name}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.125rem', flexWrap: 'wrap' }}>
            {pr.location && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                📍 {pr.location}
              </span>
            )}
            {dateRange && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                📅 {dateRange}
              </span>
            )}
            {pr.utilization != null && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                {pr.utilization}% Auslastung
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onEdit}
            title="Bearbeiten"
            style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--color-border)', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button
            onClick={onDelete}
            title="Löschen"
            style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid transparent', cursor: 'pointer', color: 'var(--color-error, #dc2626)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
              <path d="M9 6V4h6v2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Aktivitätsverlauf */}
          <div>
            <div style={{ ...sectionLabel, marginBottom: '0.375rem' }}>
              Verlauf ({activitiesLoading ? '...' : linkedActivities.length})
            </div>
            {activitiesLoading ? (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Laden...</div>
            ) : linkedActivities.length === 0 ? (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                Keine Aktivitäten
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: 140, overflow: 'auto' }}>
                {linkedActivities.map((act) => {
                  const typeBg = act.type === 'EMAIL' ? '#dbeafe' : act.type === 'CALL' ? '#dcfce7' : act.type === 'MEETING' ? '#fef3c7' : act.type === 'TASK' ? '#ede9fe' : '#f3f4f6';
                  const typeColor = act.type === 'EMAIL' ? '#1e40af' : act.type === 'CALL' ? '#166534' : act.type === 'MEETING' ? '#92400e' : act.type === 'TASK' ? '#5b21b6' : '#6b7280';
                  return (
                    <div key={act.id} style={{ padding: '0.3rem 0.625rem', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', borderLeft: `3px solid ${typeColor}`, fontSize: '0.8125rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                          <span style={{ fontSize: '0.625rem', fontWeight: 600, padding: '0.0625rem 0.375rem', borderRadius: '999px', background: typeBg, color: typeColor, flexShrink: 0 }}>
                            {ACTIVITY_TYPE_LABELS[act.type]}
                          </span>
                          <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {act.subject}
                          </span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                          {formatDateTime(act.scheduledAt ?? act.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pre-Sales Belege — Level 1 direkt */}
          {lexwareEnabled && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                <span style={sectionLabel}>
                  Angebote &amp; Auftragsbestätigungen ({preSalesVouchers.length})
                </span>
                <button
                  type="button"
                  onClick={() => setPreSalesPicker(true)}
                  style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600 }}
                >
                  + Beleg zuordnen
                </button>
              </div>
              {preSalesVouchers.length === 0 ? (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Keine Angebote / Auftragsbestätigungen zugeordnet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {preSalesVouchers.map((v) => (
                    <VoucherRow
                      key={v.id}
                      voucher={v}
                      onUnlink={() => unlinkMut.mutate({ projectRequestId: pr.id, voucherId: v.id })}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Level 1.1 — Projektbericht (Clockodo) */}
          <ProjektberichtBlock
            companyId={companyId}
            pr={pr}
            lexwareEnabled={lexwareEnabled}
          />
        </div>
      )}

      {preSalesPicker && (
        <VoucherLinkPicker
          companyId={companyId}
          projectRequestId={pr.id}
          typeFilter="pre-sales"
          onClose={() => setPreSalesPicker(false)}
        />
      )}
    </div>
  );
}

// ── Main Component ──

interface Props {
  companyId: string;
}

const PHASE_TABS: { key: ProjectPhase; label: string }[] = [
  { key: 'INQUIRY', label: 'Anfragen' },
  { key: 'ACTIVE', label: 'Aktuelle Projekte' },
  { key: 'COMPLETED', label: 'Abgeschlossene Projekte' },
];

export function ProjectRequestsTab({ companyId }: Props) {
  const { data, isLoading } = useProjectRequests(companyId);
  const deleteMut = useDeleteProjectRequest(companyId);
  const { isModuleEnabled } = useCrmSettings();
  const lexwareEnabled = isModuleEnabled('lexware');

  const [activePhase, setActivePhase] = useState<ProjectPhase>('INQUIRY');
  const [isFormOpen, setFormOpen] = useState(false);
  const [editingPr, setEditingPr] = useState<CompanyProjectRequest | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const allItems: CompanyProjectRequest[] = data?.data ?? [];

  // Items whose status has no phase assignment fall into INQUIRY
  const itemsByPhase = (phase: ProjectPhase) =>
    allItems.filter((pr) => (pr.status?.phase ?? 'INQUIRY') === phase);

  const items = itemsByPhase(activePhase);

  const handleDelete = (pr: CompanyProjectRequest) => {
    if (window.confirm(`Projekt "${pr.title}" wirklich löschen?`)) {
      deleteMut.mutate(pr.id);
    }
  };

  return (
    <div className={styles.card} style={{ padding: 0, overflow: 'hidden' }}>
      {/* Card Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1rem 1.5rem', gap: '0.5rem',
      }}>
        <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>
          Projekte ({allItems.length})
        </h2>
        <button
          onClick={() => { setEditingPr(null); setFormOpen(true); }}
          style={{
            padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
            background: 'var(--color-primary)', color: 'white',
            border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Neues Projekt
        </button>
      </div>

      {/* Phase-Reiter */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '1px solid var(--color-border)',
        padding: '0 1.5rem',
      }}>
        {PHASE_TABS.map((tab) => {
          const count = itemsByPhase(tab.key).length;
          const isActive = activePhase === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActivePhase(tab.key)}
              style={{
                padding: '0.5rem 0.875rem',
                fontSize: '0.8125rem', fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)',
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                marginBottom: '-1px',
                whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: '0.375rem',
              }}
            >
              {tab.label}
              {count > 0 && (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600,
                  background: isActive ? 'var(--color-primary)' : 'var(--color-border)',
                  color: isActive ? 'white' : 'var(--color-text-muted)',
                  borderRadius: '999px', padding: '0.0625rem 0.375rem',
                  minWidth: 18, textAlign: 'center',
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p style={{ margin: '1rem 1.5rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Laden...</p>
      ) : items.length === 0 ? (
        <div style={{
          margin: '1rem 1.5rem 1.5rem',
          border: '2px dashed var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: '2rem 1rem',
          textAlign: 'center',
          color: 'var(--color-text-muted)',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto', opacity: 0.4 }}>
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
          </svg>
          <p style={{ margin: '0.75rem 0 1rem', fontSize: '0.875rem' }}>
            {activePhase === 'INQUIRY' && 'Noch keine Anfragen vorhanden.'}
            {activePhase === 'ACTIVE' && 'Keine aktiven Projekte.'}
            {activePhase === 'COMPLETED' && 'Noch keine abgeschlossenen Projekte.'}
          </p>
          {activePhase === 'INQUIRY' && (
            <button
              onClick={() => { setEditingPr(null); setFormOpen(true); }}
              style={{
                padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
                background: 'var(--color-primary)', color: 'white',
                border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              }}
            >
              Erste Anfrage anlegen
            </button>
          )}
        </div>
      ) : (
        <div style={{ padding: '1rem 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {items.map((pr) => (
            <ProjectListItem
              key={pr.id}
              pr={pr}
              companyId={companyId}
              isExpanded={!!expandedRows[pr.id]}
              onToggleExpand={() => setExpandedRows((prev) => ({ ...prev, [pr.id]: !prev[pr.id] }))}
              onEdit={() => { setEditingPr(pr); setFormOpen(true); }}
              onDelete={() => handleDelete(pr)}
              lexwareEnabled={lexwareEnabled}
            />
          ))}
        </div>
      )}

      <ProjectRequestFormModal
        isOpen={isFormOpen}
        onClose={() => { setFormOpen(false); setEditingPr(null); }}
        companyId={companyId}
        projectRequest={editingPr}
      />
    </div>
  );
}

// ── Form Modal ──

function ProjectRequestFormModal({
  isOpen,
  onClose,
  companyId,
  projectRequest,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  projectRequest: CompanyProjectRequest | null;
}) {
  const isEdit = !!projectRequest;
  const createMut = useCreateProjectRequest(companyId);
  const updateMut = useUpdateProjectRequest(companyId);
  const { data: statusesData } = useProjectRequestStatuses();
  const statuses: ProjectRequestStatus[] = statusesData?.data ?? [];

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [duration, setDuration] = useState('');
  const [projectStart, setProjectStart] = useState('');
  const [projectEnd, setProjectEnd] = useState('');
  const [utilization, setUtilization] = useState('100');
  const [remotePercentage, setRemotePercentage] = useState('0');
  const [remoteHourlyRate, setRemoteHourlyRate] = useState<string | number>('');
  const [onsitePercentage, setOnsitePercentage] = useState('100');
  const [onsiteHourlyRate, setOnsiteHourlyRate] = useState<string | number>('');
  const [contractType, setContractType] = useState('');
  const [statusId, setStatusId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle(projectRequest?.title ?? '');
      setDescription(projectRequest?.description ?? '');
      setLocation(projectRequest?.location ?? '');
      setDuration(projectRequest?.duration ?? '');
      setProjectStart(projectRequest?.projectStart?.substring(0, 10) ?? '');
      setProjectEnd(projectRequest?.projectEnd?.substring(0, 10) ?? '');
      setUtilization(String(projectRequest?.utilization ?? 100));
      setRemotePercentage(String(projectRequest?.remotePercentage ?? 0));
      setRemoteHourlyRate(projectRequest?.remoteHourlyRate ?? '');
      setOnsitePercentage(String(projectRequest?.onsitePercentage ?? 100));
      setOnsiteHourlyRate(projectRequest?.onsiteHourlyRate ?? '');
      setContractType(projectRequest?.contractType ?? '');
      setStatusId(projectRequest?.statusId ?? '');
      setNotes(projectRequest?.notes ?? '');
      setError('');
    }
  }, [isOpen, projectRequest]);

  const isPending = createMut.isPending || updateMut.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) { setError('Titel ist ein Pflichtfeld.'); return; }

    const payload: CreateCompanyProjectRequestPayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      duration: duration.trim() || undefined,
      projectStart: projectStart || null,
      projectEnd: projectEnd || null,
      utilization: utilization ? parseInt(utilization) : undefined,
      remotePercentage: remotePercentage ? parseInt(remotePercentage) : undefined,
      remoteHourlyRate: remoteHourlyRate ? parseFloat(String(remoteHourlyRate)) : undefined,
      onsitePercentage: onsitePercentage ? parseInt(onsitePercentage) : undefined,
      onsiteHourlyRate: onsiteHourlyRate ? parseFloat(String(onsiteHourlyRate)) : undefined,
      contractType: contractType.trim() || undefined,
      statusId: statusId || undefined,
      notes: notes.trim() || undefined,
    };

    if (isEdit && projectRequest) {
      updateMut.mutate(
        { id: projectRequest.id, data: payload },
        { onSuccess: () => onClose(), onError: () => setError('Fehler beim Speichern') },
      );
    } else {
      createMut.mutate(payload, {
        onSuccess: () => onClose(),
        onError: () => setError('Fehler beim Erstellen'),
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Projekt bearbeiten' : 'Neues Projekt'}
      maxWidth="720px"
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <div style={{
            padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 'var(--radius-sm)', color: 'var(--color-error)',
            fontSize: '0.875rem', marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Titel *</label>
            <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Projekttitel..." required autoFocus />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select style={{ ...inputStyle, height: 38 }} value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              <option value="">— Kein Status —</option>
              {statuses.slice().sort((a, b) => a.name.localeCompare(b.name, 'de')).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Projektbeschreibung</label>
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Beschreibung..."
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Einsatzort</label>
            <input style={inputStyle} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z.B. Köln, Remote" />
          </div>
          <div>
            <label style={labelStyle}>Laufzeit</label>
            <input style={inputStyle} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="z.B. 6 Monate" />
          </div>
          <div>
            <label style={labelStyle}>Auslastung (%)</label>
            <input style={inputStyle} type="number" min="0" max="100" value={utilization} onChange={(e) => setUtilization(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Projekt Start</label>
            <input style={inputStyle} type="date" value={projectStart} onChange={(e) => setProjectStart(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Remote Anteil (%)</label>
            <input style={inputStyle} type="number" min="0" max="100" value={remotePercentage} onChange={(e) => setRemotePercentage(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Remote Stundensatz (€)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" value={remoteHourlyRate} onChange={(e) => setRemoteHourlyRate(e.target.value)} placeholder="0.00" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Projekt Ende</label>
            <input style={inputStyle} type="date" value={projectEnd} onChange={(e) => setProjectEnd(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Vor-Ort Anteil (%)</label>
            <input style={inputStyle} type="number" min="0" max="100" value={onsitePercentage} onChange={(e) => setOnsitePercentage(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Vor-Ort Stundensatz (€)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" value={onsiteHourlyRate} onChange={(e) => setOnsiteHourlyRate(e.target.value)} placeholder="0.00" />
          </div>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Vertragsart</label>
          <input style={inputStyle} value={contractType} onChange={(e) => setContractType(e.target.value)} placeholder="z.B. ANÜ, Werk, Dienst" />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Notizen</label>
          <textarea
            style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Optionale Notizen..."
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button
            type="button" onClick={onClose} disabled={isPending}
            style={{
              padding: '0.5rem 1rem', background: 'transparent',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem', cursor: 'pointer', color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            type="submit" disabled={isPending}
            style={{
              padding: '0.5rem 1rem', background: 'var(--color-primary)', color: 'white',
              border: 'none', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem',
              fontWeight: 600, cursor: isPending ? 'wait' : 'pointer', opacity: isPending ? 0.7 : 1,
            }}
          >
            {isPending ? 'Speichern...' : isEdit ? 'Speichern' : 'Erstellen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
